import React, { useState } from 'react';
import {
    Avatar,
    Box,
    Chip,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GroupsIcon from '@mui/icons-material/Groups';
import SchoolIcon from '@mui/icons-material/School';
import ClassIcon from '@mui/icons-material/Class';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import {
    BandBar,
    BandKey,
    DashboardError,
    DashboardHeader,
    DashboardLoading,
    EmptyNote,
    Panel,
    PendingWidgets,
    PrincipalData,
    SectionTable,
    StatCard,
    findWidget,
    initialsOf,
    useSchoolDashboard,
    widgetRows,
} from './schoolDashboard';
import { AcademicImpact } from './academicImpact';
import { TeacherAdoptionPanel } from './teacherAdoption';
import { BandStudentsDrawer, SectionBandBars, bandEntries, entryCounts } from './riskBands';

/**
 * WIZI SCHOOL COMMAND CENTRE -- the school administrator's dashboard.
 *
 * Whole-school scope: everything in this school's database, and nothing from
 * any other school's, which the database boundary guarantees rather than any
 * filter written here.
 *
 * Two measured tiles are now rendered from the response's `widgets` array,
 * which this screen previously discarded: the weekly accuracy trend behind
 * `academic_impact` (with its subject and section splits, in academicImpact.tsx),
 * and the risk bands behind `intervention_counts`. The third
 * filled widget, `school_wide_gaps`, is keyed on `topic_id` with no topic names
 * anywhere in the payload, so it is left out -- a list of numeric topic ids is
 * worse than an honest absence.
 *
 * The "Setup gaps" card still earns its place during onboarding. A section with
 * no class teacher or a teacher assigned to nothing is real, actionable, and
 * precisely what a school administrator is for before any learning data exists.
 */

const SetupGaps = ({ coverage }: { coverage: PrincipalData['coverage'] }) => {
    const groups = [
        {
            title: 'Sections with no class teacher',
            items: coverage.sections_without_class_teacher,
            fix: 'Assign a class teacher so someone owns this section.',
        },
        {
            title: 'Sections with no students',
            items: coverage.sections_without_students,
            fix: 'Enrol students, or remove the section if it is not running.',
        },
        {
            title: 'Teachers with no assignment',
            items: coverage.teachers_without_assignment,
            fix: 'A teacher with no assignment can see nothing at all.',
        },
    ].filter(g => g.items.length > 0);

    const total = groups.reduce((sum, g) => sum + g.items.length, 0);

    return (
        <Panel
            title="Setup gaps"
            tone={groups.length === 0 ? 'success' : 'warning'}
            icon={<BuildCircleIcon fontSize="small" />}
            action={
                total > 0 ? (
                    <Chip size="small" color="warning" label={total} sx={{ fontWeight: 700 }} />
                ) : (
                    <Chip size="small" color="success" label="Complete" />
                )
            }
        >
            {groups.length === 0 ? (
                <EmptyNote>
                    Every section has a class teacher and students, and every teacher has at
                    least one assignment. Setup is complete.
                </EmptyNote>
            ) : (
                <Stack spacing={2}>
                    {groups.map(g => (
                        <Box key={g.title}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {g.title}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, my: 0.75 }}>
                                {g.items.map(item => (
                                    <Chip
                                        key={item}
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        label={item}
                                    />
                                ))}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {g.fix}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            )}
        </Panel>
    );
};

const TeacherTable = ({ teachers }: { teachers: PrincipalData['teachers'] }) => {
    const theme = useTheme();

    return (
        <Panel
            title="Teachers"
            icon={<PersonIcon fontSize="small" />}
            action={<Chip size="small" label={`${teachers.length} on roster`} />}
        >
            {teachers.length === 0 ? (
                <EmptyNote>No teachers on the roster yet.</EmptyNote>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">
                                Sections
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {teachers.map(t => (
                            <TableRow key={t.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                <TableCell>
                                    <Stack direction="row" spacing={1.25} alignItems="center">
                                        <Avatar
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                fontSize: 12,
                                                fontWeight: 700,
                                                bgcolor: alpha(theme.palette.primary.main, 0.14),
                                                color: theme.palette.primary.main,
                                            }}
                                        >
                                            {initialsOf(t.full_name)}
                                        </Avatar>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {t.full_name}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        {t.email || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {t.section_count === 0 ? (
                                        <Chip size="small" color="warning" variant="outlined" label="0" />
                                    ) : (
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {t.section_count}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {t.is_class_teacher ? (
                                        <Chip size="small" color="primary" label="Class teacher" />
                                    ) : (
                                        <Chip size="small" variant="outlined" label="Subject teacher" />
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Panel>
    );
};

const SECTIONS_ANCHOR = 'principal-sections';

const PrincipalDashboard = () => {
    const { data, loading, error } = useSchoolDashboard<PrincipalData>('principal');
    const navigate = useNavigate();
    const [drill, setDrill] = useState<{ open: boolean; band: BandKey | 'all'; section: string }>({
        open: false,
        band: 'intervention',
        section: '',
    });

    if (loading) return <DashboardLoading />;
    if (error) return <DashboardError message={error} />;
    if (!data) return <DashboardError message="No dashboard data was returned." />;

    const { roster } = data;

    const riskWidget = findWidget(data.widgets, 'intervention_counts');
    const riskRows = widgetRows(data.widgets, 'intervention_counts');
    // Banded over the roster, so a student with no activity at all is counted
    // (as "not enough data") and the bar adds up to the student list behind it.
    const entries = bandEntries(riskRows, data.students || []);
    const counts = entryCounts(entries);
    const measured = counts.healthy + counts.watch + counts.intervention;
    const openBand = (band: BandKey | 'all', section = '') => setDrill({ open: true, band, section });
    const scrollToSections = () =>
        document.getElementById(SECTIONS_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    return (
        <Box sx={{ mt: 1 }}>
            <DashboardHeader
                subtitle="WiZi School Command Centre"
                title="School overview"
                school={data.school}
                year={data.academic_year}
                icon={<AccountBalanceIcon />}
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                <StatCard
                    label="Students"
                    value={roster.students}
                    hint="Active enrolments"
                    icon={<GroupsIcon fontSize="small" />}
                    tone="primary"
                    onClick={() => navigate('/school-student')}
                    actionLabel="Open students"
                />
                <StatCard
                    label="Teachers"
                    value={roster.teachers}
                    hint="Active staff"
                    icon={<SchoolIcon fontSize="small" />}
                    tone="secondary"
                    onClick={() => navigate('/school-teacher')}
                    actionLabel="Open teachers"
                />
                <StatCard
                    label="Sections"
                    value={roster.sections}
                    hint="This academic year"
                    icon={<ClassIcon fontSize="small" />}
                    tone="info"
                    onClick={() => navigate('/class-section')}
                    actionLabel="Open sections"
                />
                <StatCard
                    label="Avg per section"
                    value={roster.avg_students_per_section ?? '—'}
                    hint={roster.sections === 0 ? 'No sections yet' : 'Students per section'}
                    icon={<GroupWorkIcon fontSize="small" />}
                    tone="success"
                    onClick={scrollToSections}
                    actionLabel="See section sizes"
                />
                <StatCard
                    label="Intervention required"
                    value={measured === 0 ? '—' : counts.intervention}
                    hint={
                        measured === 0
                            ? 'No attempt data yet'
                            : `of ${measured} students with enough data`
                    }
                    icon={<ReportProblemIcon fontSize="small" />}
                    tone="error"
                    onClick={measured === 0 ? undefined : () => openBand('intervention')}
                    actionLabel="See who"
                    progress={measured === 0 ? null : (counts.intervention / measured) * 100}
                />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5, alignItems: 'stretch' }}>
                <Box sx={{ flex: '1 1 380px', minWidth: 320 }}>
                    <AcademicImpact widgets={data.widgets} sections={data.sections} />
                </Box>
                <Box sx={{ flex: '1 1 380px', minWidth: 320 }}>
                    <Panel
                        title="Students by risk band"
                        tone="warning"
                        icon={<DonutLargeIcon fontSize="small" />}
                        subtitle={
                            riskWidget?.available
                                ? 'Accuracy on assigned work, last 30 days. Click a number, a bar or a section to see the students.'
                                : riskWidget?.reason || 'Waiting on attempt data.'
                        }
                    >
                        <BandBar counts={counts} onSelect={band => openBand(band)} />
                        <SectionBandBars entries={entries} onSelect={openBand} />
                    </Panel>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5, alignItems: 'stretch' }}>
                <Box id={SECTIONS_ANCHOR} sx={{ flex: '2 1 420px', minWidth: 320, scrollMarginTop: 16 }}>
                    <Panel title="Sections" icon={<ClassIcon fontSize="small" />}>
                        <SectionTable
                            sections={data.sections}
                            emptyMessage="No sections have been created for this academic year."
                        />
                    </Panel>
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: 280 }}>
                    <SetupGaps coverage={data.coverage} />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5, alignItems: 'stretch' }}>
                <Box sx={{ flex: '1 1 440px', minWidth: 320 }}>
                    <TeacherTable teachers={data.teachers} />
                </Box>
                <Box sx={{ flex: '1 1 480px', minWidth: 320 }}>
                    <TeacherAdoptionPanel adoption={data.teacher_adoption} />
                </Box>
            </Box>

            <PendingWidgets pending={data.pending} />

            <BandStudentsDrawer
                open={drill.open}
                onClose={() => setDrill(d => ({ ...d, open: false }))}
                entries={entries}
                initialBand={drill.band}
                initialSection={drill.section}
            />
        </Box>
    );
};

export default PrincipalDashboard;
